import { LocationCityOutlined } from "@mui/icons-material";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "src/app/contexts/auth-context";
import LocationScopePicker, {
  LocationScope,
  LocationScopeGroup,
} from "src/app/pages/common/location-scope-picker/LocationScopePicker";
import { useLocationScope } from "src/app/pages/common/location-scope-picker/useLocationScope";

type Props = {
  onSwitchLocation: (locationId: number) => void;
  className?: string;
  disablePortal?: boolean;
};

/**
 * Header location control. Picking one assigned location switches the working location as before;
 * picking several or all locations widens the payments and sales vouchers lists without switching.
 */
const HeaderLocationPicker = ({
  onSwitchLocation,
  className,
  disablePortal,
}: Props) => {
  const { t } = useTranslation();
  const currentUser = useContext(AuthContext)?.authContextData?.currentUser;
  const {
    groups: scopeGroups,
    scope,
    defaultScope,
    setScope,
    sessionCompanyId,
    sessionLocationId,
  } = useLocationScope();

  const assigned = useMemo(
    () => currentUser?.locations ?? [],
    [currentUser?.locations]
  );

  const groups = useMemo<LocationScopeGroup[]>(() => {
    // Another business unit can only be entered through an assigned location of it.
    const switchable = new Set(assigned.map((l) => Number(l.companyId)));
    const accessible = scopeGroups.filter(
      (g) => g.companyId === sessionCompanyId || switchable.has(g.companyId)
    );
    if (accessible.length > 0) return accessible;
    // Until the scopes load (or if they fail) the assigned locations still allow switching.
    return (currentUser?.companies ?? [])
      .map((company) => ({
        companyId: Number(company.id),
        companyName: company.name,
        sessionLocationId:
          Number(company.id) === sessionCompanyId ? sessionLocationId : null,
        locations: assigned
          .filter((l) => Number(l.companyId) === Number(company.id))
          .map((l) => ({ id: Number(l.id), name: l.name, code: l.code })),
      }))
      .filter((g) => g.locations.length > 0);
  }, [
    scopeGroups,
    assigned,
    currentUser?.companies,
    sessionCompanyId,
    sessionLocationId,
  ]);

  const value = useMemo<LocationScope>(
    () =>
      scope ?? {
        companyId: sessionCompanyId,
        locationIds: sessionLocationId ? [sessionLocationId] : [],
      },
    [scope, sessionCompanyId, sessionLocationId]
  );

  const targetWorkingLocation = (next: LocationScope): number | undefined => {
    const assignedIds = assigned
      .filter((l) => Number(l.companyId) === next.companyId)
      .map((l) => Number(l.id));
    if (next.locationIds.length === 1 && assignedIds.includes(next.locationIds[0]))
      return next.locationIds[0] === sessionLocationId
        ? undefined
        : next.locationIds[0];
    if (next.companyId === sessionCompanyId) return undefined;
    return next.locationIds.find((id) => assignedIds.includes(id)) ?? assignedIds[0];
  };

  const apply = (next: LocationScope) => {
    const target = targetWorkingLocation(next);
    setScope(next, target ?? sessionLocationId);
    if (target !== undefined) onSwitchLocation(target);
  };

  const applyHint = (draft: LocationScope) => {
    const target = targetWorkingLocation(draft);
    if (target === undefined)
      return draft.locationIds.length === 1 &&
        draft.locationIds[0] === sessionLocationId
        ? undefined
        : t("modules.payments.location-scope.lists-hint");
    return t("modules.payments.location-scope.working-location-change", {
      name: assigned.find((l) => Number(l.id) === target)?.name ?? "",
    });
  };

  const totalLocations = groups.reduce((n, g) => n + g.locations.length, 0);
  if (groups.length <= 1 && totalLocations <= 1) {
    return (
      <div className="relative mr-1 xl:mr-12 flex items-center justify-center w-[10rem] xl:w-[18rem]">
        <LocationCityOutlined fontSize="large" />
        <h2 className="ml-2 text-[15px] text-[#274867] w-[120px] truncate overflow-hidden whitespace-nowrap">
          {currentUser?.location?.name ?? assigned[0]?.name ?? ""}
        </h2>
      </div>
    );
  }

  return (
    <div className="masterHeader__right flex items-center z-[100] select-none space-x-3">
      <div className={className}>
        <LocationScopePicker
          groups={groups}
          value={value}
          onChange={apply}
          defaultScope={defaultScope}
          currentLocationId={sessionLocationId}
          applyHint={applyHint}
          disablePortal={disablePortal}
        />
      </div>
    </div>
  );
};

export default HeaderLocationPicker;
